import { useCallback, useEffect, useState } from 'react';
import { apiJson } from '../api/client.js';
import { SearchableSelect } from './SearchableSelect.jsx';

const PAGE_TYPE_LABELS = {
  checkout_success: 'Checkout success (used by Stripe after payment)',
  checkout_cancel: 'Checkout canceled',
  portal_return: 'Billing portal return',
  landing: 'Landing / subscribe page',
};

const EMPTY_LANDING = {
  page_type: 'landing',
  name: '',
  slug: '',
  html_content: '',
  subscription_plan_id: '',
};

export function StripePagesSection({ stripeConfigured }) {
  const [pages, setPages] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [pagesRes, plansRes] = await Promise.all([
        apiJson('/api/integrations/stripe/pages'),
        apiJson('/api/billing/plans').catch(() => []),
      ]);
      setPages(pagesRes.pages || []);
      setPlaceholders(pagesRes.placeholders || []);
      setPlans(Array.isArray(plansRes) ? plansRes : plansRes.plans || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const ensureDefaults = async () => {
    try {
      const res = await apiJson('/api/integrations/stripe/pages/ensure-defaults', { method: 'POST' });
      setPages(res.pages || []);
    } catch (e) {
      setError(e.message);
    }
  };

  const openEdit = (page) => {
    setEditing(page.id);
    setForm({
      name: page.name,
      slug: page.slug,
      html_content: page.html_content,
      subscription_plan_id: page.subscription_plan_id ? String(page.subscription_plan_id) : '',
    });
  };

  const openNewLanding = () => {
    setEditing('new');
    setForm({ ...EMPTY_LANDING, html_content: defaultLandingHtml() });
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const body = {
        name: form.name,
        html_content: form.html_content,
        subscription_plan_id: form.subscription_plan_id ? Number(form.subscription_plan_id) : null,
      };
      if (editing === 'new') {
        body.page_type = 'landing';
        body.slug = form.slug;
        await apiJson('/api/integrations/stripe/pages', { method: 'POST', body });
      } else {
        if (pages.find((p) => p.id === editing)?.page_type === 'landing') {
          body.slug = form.slug;
        }
        await apiJson(`/api/integrations/stripe/pages/${editing}`, { method: 'PUT', body });
      }
      setEditing(null);
      setForm(null);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this page?')) {
      return;
    }
    try {
      await apiJson(`/api/integrations/stripe/pages/${id}`, { method: 'DELETE' });
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  if (!stripeConfigured) {
    return (
      <p className="muted small" style={{ marginTop: '1rem' }}>
        Save your Stripe keys above to create HTML pages and auto-generated checkout links.
      </p>
    );
  }

  return (
    <section className="card-panel" style={{ marginTop: '1rem' }}>
      <h3 style={{ marginTop: 0 }}>Stripe HTML pages</h3>
      <p className="muted small">
        Create HTML pages for your agency. Success, cancel, and portal return URLs are wired into Stripe
        automatically. Landing pages with a plan get a checkout link — use <code>{'{{checkout_link}}'}</code> in
        your HTML.
      </p>

      {loading ? <p className="muted">Loading pages…</p> : null}
      {error ? <p className="err">{error}</p> : null}

      {placeholders.length > 0 ? (
        <ul className="muted small" style={{ paddingLeft: '1.25rem' }}>
          {placeholders.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      ) : null}

      <div className="row-actions" style={{ marginBottom: '1rem', flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => void ensureDefaults()}>
          Create default pages
        </button>
        <button type="button" className="btn btn-outline-primary btn-sm" onClick={openNewLanding}>
          New landing page
        </button>
      </div>

      {pages.map((page) => (
        <div
          key={page.id}
          className="card-panel"
          style={{ marginBottom: '0.75rem', background: 'var(--bs-light, #f8f9fa)' }}
        >
          <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
            <div>
              <strong>{page.name}</strong>
              <div className="muted small">{PAGE_TYPE_LABELS[page.page_type] || page.page_type}</div>
            </div>
            {page.used_by_stripe ? (
              <span className="badge text-bg-info">Stripe redirect</span>
            ) : null}
          </div>
          <p className="small" style={{ margin: '0.5rem 0 0' }}>
            Public URL:{' '}
            <a href={page.public_url} target="_blank" rel="noreferrer">
              {page.public_url}
            </a>
          </p>
          {page.checkout_url ? (
            <p className="small" style={{ margin: '0.25rem 0 0' }}>
              Checkout link (auto):{' '}
              <a href={page.checkout_url} target="_blank" rel="noreferrer">
                {page.checkout_url}
              </a>
            </p>
          ) : null}
          <div className="row-actions" style={{ marginTop: '0.5rem' }}>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => openEdit(page)}>
              Edit HTML
            </button>
            {page.page_type === 'landing' ? (
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => void remove(page.id)}>
                Delete
              </button>
            ) : null}
          </div>
        </div>
      ))}

      {editing && form ? (
        <div className="card-panel" style={{ marginTop: '1rem' }}>
          <h4 style={{ marginTop: 0 }}>{editing === 'new' ? 'New landing page' : 'Edit page'}</h4>
          <div className="stack">
            <label className="field">
              <span>Name</span>
              <input
                className="form-control"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </label>
            {editing === 'new' || pages.find((p) => p.id === editing)?.page_type === 'landing' ? (
              <label className="field">
                <span>URL slug</span>
                <input
                  className="form-control"
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  placeholder="my-offer"
                />
              </label>
            ) : null}
            {(editing === 'new' || pages.find((p) => p.id === editing)?.page_type === 'landing') && plans.length > 0 ? (
              <label className="field">
                <span>Subscription plan (generates checkout link)</span>
                <SearchableSelect
                  className="form-select"
                  value={form.subscription_plan_id}
                  onChange={(e) => setForm({ ...form, subscription_plan_id: e.target.value })}
                >
                  <option value="">— Select plan —</option>
                  {plans.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name} ({plan.slug})
                    </option>
                  ))}
                </SearchableSelect>
              </label>
            ) : null}
            <label className="field">
              <span>HTML</span>
              <textarea
                className="form-control"
                rows={14}
                value={form.html_content}
                onChange={(e) => setForm({ ...form, html_content: e.target.value })}
                spellCheck={false}
              />
            </label>
          </div>
          <div className="row-actions" style={{ marginTop: '1rem' }}>
            <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void save()}>
              {saving ? 'Saving…' : 'Save page'}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => {
                setEditing(null);
                setForm(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function defaultLandingHtml() {
  return `<!DOCTYPE html>
<html lang="en">
<head><meta charset="utf-8"><title>{{organization_name}} — Subscribe</title></head>
<body>
  <h1>{{organization_name}}</h1>
  <p><a href="{{checkout_link}}">Subscribe now</a></p>
</body>
</html>`;
}
